import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Timer, SignIn } from "@phosphor-icons/react";

const IDLE_MS = Number(localStorage.getItem("inspire.idleMs")) || 60 * 60 * 1000;
const WARN_MS = Math.min(60 * 1000, IDLE_MS / 2);
const KEY = "inspire.lastActivity";
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

const lastActivity = () => Number(localStorage.getItem(KEY)) || Date.now();
const touch = () => localStorage.setItem(KEY, String(Date.now()));

const relogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const IdleTimeout = () => {
  const [phase, setPhase] = useState("active"); // active | warning | expired
  const [secondsLeft, setSecondsLeft] = useState(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    touch();
    let lastWrite = 0;
    const onActivity = () => {
      if (phaseRef.current !== "active") return;
      const now = Date.now();
      if (now - lastWrite > 1000) { lastWrite = now; touch(); }
    };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = setInterval(() => {
      if (phaseRef.current === "expired") return;
      const idle = Date.now() - lastActivity();
      if (idle >= IDLE_MS) {
        setPhase("expired");
        api.post("/auth/logout").catch(() => {});
      } else if (idle >= IDLE_MS - WARN_MS) {
        setPhase("warning");
        setSecondsLeft(Math.ceil((IDLE_MS - idle) / 1000));
      } else if (phaseRef.current === "warning") {
        setPhase("active"); // another tab kept the session alive
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, []);

  const staySignedIn = () => { touch(); setPhase("active"); };
  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, "0");

  return (
    <AlertDialog open={phase !== "active"}>
      <AlertDialogContent className="rounded-3xl" data-testid={phase === "expired" ? "idle-expired-dialog" : "idle-warning-dialog"}>
        <AlertDialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-500 flex items-center justify-center mb-2">
            <Timer size={24} weight="duotone" />
          </div>
          <AlertDialogTitle className="font-heading text-2xl">
            {phase === "expired" ? "Session expired" : "Are you still there?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {phase === "expired"
              ? "You were signed out after 60 minutes of inactivity to keep client data safe. Please sign in again to continue."
              : "You've been inactive for a while. For security you'll be signed out in"}
          </AlertDialogDescription>
          {phase === "warning" && (
            <p className="font-heading text-5xl font-semibold tabular-nums text-amber-500 pt-2" data-testid="idle-countdown">
              {mins}:{secs}
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {phase === "expired" ? (
            <Button onClick={relogin} className="rounded-full gap-2" data-testid="idle-relogin-btn">
              <SignIn size={16} weight="bold" /> Sign in again
            </Button>
          ) : (
            <Button onClick={staySignedIn} className="rounded-full" data-testid="idle-stay-signed-in-btn">
              Stay signed in
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default IdleTimeout;
